/*
 * Copyright (c) 2026 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

// Component imports
import { Col, Form, Input, Radio, Row, Select, Switch, Typography } from 'antd';

// Misc imports
import { INTEGRATION_CATEGORY } from '../../../../constants/integrations';
import { useFetchIntegrationsQuery } from '../../../../store/integrations/integrationsApi';
import userHasEditAccess from '../../../../utils/misc/hasEditAccess';
import useTheme from '../../../../utils/hooks/useTheme';

const FORM_CONFIGURATION_LAYOUT_PATH = ['settings', 'layout', 'microsoftWebChat', 'configuration'];

const BotDeploymentsWebchatSpeechForm = ({
  formData = {},
  userRoles = {},
  isCreateMode = false,
  form
}) => {
  const { t } = useTranslation();
  const { formProps } = useTheme();
  const hasEditAccess = isCreateMode ? true : userHasEditAccess(
    formData?.tenant?.organizationId,
    userRoles?.organizationRoles,
    formData?.tenant?.id,
    userRoles?.tenantRoles,
    true
  );

  const orgId = formData?.tenant?.organizationId;

  const { data: integrationsData, isFetching: isFetchingIntegrations } = useFetchIntegrationsQuery(
    { orgId, urlParams: { category: INTEGRATION_CATEGORY.SPEECH, enabled: true } },
    { skip: !orgId }
  );

  const integrationOptions = (integrationsData?.items ?? []).map(i => ({ label: i.name, value: i.id }));

  return (
    <Row gutter={formProps.rowGutter}>
      <Col xs={24}>
        <Typography.Text>
          {t('Speech Settings')}
        </Typography.Text>
        <Typography.Paragraph className="fs-sm" type="secondary">
          {t('Enable speech-to-text and text-to-speech in the webchat widget.')}
        </Typography.Paragraph>
      </Col>

      <Col xs={24}>
        <Form.Item
          label={t('Enable Speech')}
          name={[...FORM_CONFIGURATION_LAYOUT_PATH, 'allowSpeechToText']}
          valuePropName="checked"
        >
          <Switch disabled={!hasEditAccess} />
        </Form.Item>
      </Col>

      <Form.Item shouldUpdate>
        {() => {
          const allowSpeechToText = form.getFieldValue([...FORM_CONFIGURATION_LAYOUT_PATH, 'allowSpeechToText']);
          if (!allowSpeechToText) {
            return null;
          }

          const speechMode = form.getFieldValue([...FORM_CONFIGURATION_LAYOUT_PATH, 'speechMode']) || 'helvia';

          return (
            <Col xs={24}>
              <Form.Item
                label={t('Speech Credentials')}
                name={[...FORM_CONFIGURATION_LAYOUT_PATH, 'speechMode']}
                initialValue="helvia"
              >
                <Radio.Group disabled={!hasEditAccess}>
                  <Radio value="helvia">{t('Helvia (global credentials)')}</Radio>
                  <Radio value="custom">{t('Custom Integration')}</Radio>
                </Radio.Group>
              </Form.Item>

              {speechMode === 'custom' && (
                <Form.Item
                  label={t('Speech Integration')}
                  name={[...FORM_CONFIGURATION_LAYOUT_PATH, 'speechIntegrationId']}
                >
                  <Select
                    placeholder={t('Select a Speech integration')}
                    loading={isFetchingIntegrations}
                    options={integrationOptions}
                    allowClear
                    disabled={!hasEditAccess}
                    getPopupContainer={triggerNode => triggerNode.parentElement}
                  />
                </Form.Item>
              )}

              <Form.Item
                label={t('TTS Voice Name')}
                name={[...FORM_CONFIGURATION_LAYOUT_PATH, 'ttsVoiceName']}
                extra={t('Optional. Override the default voice (e.g. en-US-JennyNeural). Falls back to locale-based voice if not found.')}
              >
                <Input
                  placeholder={t('e.g. en-US-JennyNeural')}
                  disabled={!hasEditAccess}
                />
              </Form.Item>
            </Col>
          );
        }}
      </Form.Item>
    </Row>
  );
};

BotDeploymentsWebchatSpeechForm.propTypes = {
  formData: PropTypes.object,
  userRoles: PropTypes.object,
  isCreateMode: PropTypes.bool,
  form: PropTypes.object
};

export default BotDeploymentsWebchatSpeechForm;
